import { ref, type Ref } from 'vue'
import { useRouter, type RouteLocationNormalizedLoaded } from 'vue-router'
import { useSubscriptionsStore } from '@/stores/subscription'

export interface UseCheckoutOptions {
  /**
   * Route to redirect after successful checkout
   */
  successRedirectRoute: string
  /**
   * Delay in ms before redirecting (default: 2000)
   */
  successRedirectDelay?: number
}

export interface UseCheckoutReturn {
  isCreatingCheckout: Ref<boolean>
  isProcessingCheckout: Ref<boolean>
  checkoutError: Ref<string | null>
  checkoutSuccess: Ref<boolean>
  createCheckout: (planId: number | string) => Promise<void>
  handleCheckoutReturn: (route: RouteLocationNormalizedLoaded) => Promise<void>
  clearError: () => void
}

/**
 * Composable for handling Stripe checkout flow
 * Manages checkout session creation and return processing
 */
export function useCheckout(options: UseCheckoutOptions): UseCheckoutReturn {
  const router = useRouter()
  const subscriptionsStore = useSubscriptionsStore()

  const { successRedirectRoute, successRedirectDelay = 2000 } = options

  // State
  const isCreatingCheckout = ref(false)
  const isProcessingCheckout = ref(false)
  const checkoutError = ref<string | null>(null)
  const checkoutSuccess = ref(false)

  /**
   * Create a checkout session and redirect to Stripe
   */
  const createCheckout = async (planId: number | string): Promise<void> => {
    checkoutError.value = null
    isCreatingCheckout.value = true

    try {
      const checkoutData = await subscriptionsStore.createCheckoutSession(planId)

      if (!checkoutData.session_url) {
        throw new Error('No session URL received from server')
      }

      window.location.href = checkoutData.session_url
    } catch (error: any) {
      isCreatingCheckout.value = false
      checkoutError.value =
        error.response?.data?.error ||
        error.message ||
        'Failed to create checkout session'
    }
  }

  /**
   * Handle return from Stripe Checkout
   */
  const handleCheckoutReturn = async (
    route: RouteLocationNormalizedLoaded
  ): Promise<void> => {
    const { session_id: sessionId, cancelled } = route.query

    // Handle cancelled checkout
    if (cancelled === 'true') {
      checkoutError.value = 'Payment was cancelled. Please try again.'
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.replace({ query: {} })
      return
    }

    // No session ID - nothing to process
    if (!sessionId) return

    isProcessingCheckout.value = true

    try {
      const result = await subscriptionsStore.retrieveCheckoutSession(
        sessionId as string
      )

      if (result.status === 'complete') {
        checkoutSuccess.value = true
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.replace({ query: {} })

        setTimeout(() => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          router.push(successRedirectRoute)
        }, successRedirectDelay)
      } else {
        checkoutError.value =
          'Checkout session is not complete. Please contact support.'
      }
    } catch (error: any) {
      checkoutError.value =
        error.message || 'Failed to process checkout session'
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.replace({ query: {} })
    } finally {
      isProcessingCheckout.value = false
    }
  }

  /**
   * Clear the checkout error
   */
  const clearError = (): void => {
    checkoutError.value = null
  }

  return {
    isCreatingCheckout,
    isProcessingCheckout,
    checkoutError,
    checkoutSuccess,
    createCheckout,
    handleCheckoutReturn,
    clearError
  }
}
